import { useReducer, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import every from 'lodash/every';
import { formPlaceholders, isValidUKCountryCode } from 'components/Address/util';
import postcodeValidatorFactory from './postcode-validation-factory';

export const useFormHook = ({ country, hasFoundAddress, isEditing, manualOnly, formValuesRef }) => {
  const isEmptyForm = formValues => {
    const values = formValues.current;

    return every(values, (value, key) => {
      return key === 'country' || isEmpty(value);
    });
  };

  const initialState = (countryValue, action = null) => {
    const { internationalPlaceholders, uKPlaceholders } = formPlaceholders;
    const isGB = isValidUKCountryCode(countryValue);
    const placeholders = isGB ? uKPlaceholders : internationalPlaceholders;
    const postcodeZipValidator = postcodeValidatorFactory(countryValue);

    const isCountryChanging = action === 'SET_PLACEHOLDERS';
    const isChangingToGBWithEmptyForm =
      isCountryChanging && isGB && !isEditing && isEmptyForm(formValuesRef);

    const showManualAddress =
      manualOnly || !isGB || isEditing || (isCountryChanging && !isChangingToGBWithEmptyForm);
    const showSummary = !manualOnly && !showManualAddress && hasFoundAddress && !isCountryChanging;
    const showCountrySelect = manualOnly || showManualAddress || isEditing || !hasFoundAddress;

    return {
      placeholders,
      postcodeZipValidator,
      showCountrySelect,
      showSummary,
      showManualAddress,
    };
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case 'SET_PLACEHOLDERS':
        return {
          ...state,
          ...initialState(action.payload, 'SET_PLACEHOLDERS'),
        };
      case 'SET_MANUAL_ADDRESS':
        return {
          ...state,
          showCountrySelect: true,
          showManualAddress: true,
          showSummary: false,
        };
      case 'SET_PREDICTIVE_ADDRESS':
        return {
          ...state,
          showCountrySelect: false,
          showManualAddress: false,
          showSummary: true,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState(country));

  const setPlaceholders = useCallback(
    countryValue => dispatch({ type: 'SET_PLACEHOLDERS', payload: countryValue }),
    [],
  );
  const setManualAddress = useCallback(() => dispatch({ type: 'SET_MANUAL_ADDRESS' }), []);
  const setPredictiveAddress = useCallback(() => dispatch({ type: 'SET_PREDICTIVE_ADDRESS' }), []);

  return [state, setPlaceholders, setManualAddress, setPredictiveAddress];
};
